import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import { Mail, Lock, Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../services/contexts/AuthContext';
import './Login.css';

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { updateUser, updateToken } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const { register, handleSubmit, formState: { errors }, watch } = useForm();
  const email = watch('email');

  // Check for error messages from URL params
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const error = urlParams.get('error');
    
    if (error) {
      switch (error) {
        case 'auth_failed':
          toast.error('Authentication failed. Please try again.');
          break;
        case 'backend_validation_failed':
          toast.error('Backend validation failed. Please contact support.');
          break;
        case 'validation_error':
          toast.error('Validation error occurred. Please try again.');
          break;
        default:
          toast.error('An error occurred during authentication.');
      }
    }
  }, [location]);


  const handleLogin = async (data) => {
    setLoading(true);
    console.log("=== FRONTEND LOGIN FLOW START ===");
    console.log("Form data received:", { email: data.email, password: "***" });
    
    if (!data.email || !data.password) {
      console.error("Validation failed: Missing email or password");
      toast.error('Please enter both email and password');
      return;
    }

    try {
      const requestBody = { email: data.email, password: data.password };
      console.log("Making API call to backend...");
      console.log("Request body:", { email: data.email, password: "***" });
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/login-with-auth0`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody)
      });

      console.log("Response received:", {
        status: response.status,
        statusText: response.statusText,
        ok: response.ok
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error("Backend error response:", errorData);
        
        let errorMessage = 'Authentication failed';
        if (errorData.detail) {
          // Map Auth0 error messages to user-friendly messages
          const auth0Error = errorData.detail.toLowerCase();
          if (auth0Error.includes('wrong email or password') || 
              auth0Error.includes('invalid_grant') ||
              auth0Error.includes('invalid credentials')) {
            errorMessage = 'Invalid email or password. Please check your credentials and try again.';
          } else if (auth0Error.includes('user not found') || 
                     auth0Error.includes('user does not exist')) {
            errorMessage = 'No account found with this email address.';
          } else if (auth0Error.includes('too many attempts') || 
                     auth0Error.includes('rate limit')) {
            errorMessage = 'Too many login attempts. Please wait a moment and try again.';
          } else if (auth0Error.includes('account locked') || 
                     auth0Error.includes('account disabled')) {
            errorMessage = 'Your account has been locked. Please contact support.';
          } else {
            // Use the original error message for other cases
            errorMessage = errorData.detail;
          }
        }
        
        console.error("Authentication failed:", errorMessage);
        toast.error(errorMessage);
        return;
      }

      const authData = await response.json();
      console.log("Response data received:", authData);
      
      // Check for MFA requirement first
      if (authData.requires_mfa && authData.mfa_token) {
        console.log("MFA required, redirecting to MFA verification...");
        
        // Store MFA data for the verification page
        const mfaData = {
          email: data.email,
          mfa_token: authData.mfa_token,
          message: authData.message
        };
        
        localStorage.setItem('mfaData', JSON.stringify(mfaData));
        
        // Redirect to MFA verification page
        navigate('/mfa-verification', { 
          state: { mfaData },
          replace: true 
        });
        
        toast.success('MFA verification required');
        return;
      }
      
      // Check if we have a successful authentication response
      if (authData.access_token && authData.user) {
        console.log("=== AUTHENTICATION SUCCESSFUL ===");
        console.log("User role:", authData.user.role);
        console.log("Redirect URL:", authData.redirect_url);
        
        // Update AuthContext with user data and token
        updateUser(authData.user);
        updateToken(authData.access_token);
        
        // Store in localStorage for persistence
        localStorage.setItem('token', authData.access_token);
        localStorage.setItem('user', JSON.stringify(authData.user));
        
        console.log("AuthContext updated successfully");
        console.log("User data stored in localStorage");
        
        // Check if user is SYSTEM_ADMIN and redirect directly
        if (authData.user.role === 'SYSTEM_ADMIN') {
          console.log("System admin detected - redirecting to system admin page");
          toast.success('Welcome System Administrator!');
          navigate('/sysadmin', { replace: true });
        } else {
          // For other roles, use the redirect URL from backend
          console.log("Non-system admin user - using backend redirect URL");
          toast.success('Login successful!');
          navigate(authData.redirect_url, { replace: true });
        }
        
        console.log("=== FRONTEND LOGIN FLOW COMPLETED SUCCESSFULLY ===");
      } else {
        console.error("Invalid response format - missing access_token or user data");
        toast.error('Invalid response from server');
      }
      
    } catch (error) {
      console.error("=== FRONTEND LOGIN FLOW FAILED ===");
      console.error("Network or parsing error:", error);
      console.error("Error details:", error);
      
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        toast.error('Network error - please check your connection');
      } else {
        toast.error('Login failed - please try again');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!email) {
      toast.error('Please enter your email address first');
      return;
    }

    try {
      setLoading(true);
      console.log("=== FORGOT PASSWORD FLOW START ===");
      console.log("Email:", email);
      
      // Call Auth0's password reset endpoint
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error("Forgot password error:", errorData);
        toast.error(errorData.detail || 'Failed to send password reset email');
        return;
      }

      const result = await response.json();
      console.log("Password reset response:", result);
      
      toast.success('Password reset email sent! Check your inbox.');
      console.log("=== FORGOT PASSWORD FLOW COMPLETED ===");
      
    } catch (error) {
      console.error("Forgot password error:", error);
      toast.error('Failed to send password reset email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{
      background: 'linear-gradient(135deg, #207CE5 0%, #008B8B 100%)'
    }}>
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-4">
            <div className="bg-white p-3 rounded-full shadow-lg">
              <img 
                src="/Vital-logo.png" 
                alt="VITAL AUDIO Logo" 
                className="h-8 w-8 object-contain"
              />
            </div>
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">Sonometrik</h1>
        </div>

        {/* Login Form */}
        <div className="glass-effect rounded-2xl p-8 shadow-xl">
          <form onSubmit={handleSubmit(handleLogin)} className="space-y-6">
            <div className="text-center mb-6">
              <h2 className="text-xl font-semibold text-white mb-2">Sign In</h2>
              <p className="text-va-primary-100 text-sm">Enter your credentials to continue</p>
              
            </div>

            <div className="space-y-4">
              {/* Email Field */}
              <div>
                <label className="block text-sm font-medium text-white mb-2">
                  Email Address
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-va-neutral-400 h-5 w-5" />
                  <input
                    type="email"
                    autoComplete="username"
                    {...register('email', { 
                      required: 'Email is required',
                      pattern: {
                        value: /\S+@\S+\.\S+/,
                        message: 'Invalid email address'
                      }
                    })}
                    className="w-full pl-10 pr-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-va-neutral-300 focus:outline-none focus:ring-2 focus:ring-brand-blue focus:border-transparent"
                    placeholder="Enter your email"
                  />
                </div>
                {errors.email && (
                  <p className="mt-1 text-sm text-red-300">
                    {typeof errors.email.message === 'string' 
                      ? errors.email.message 
                      : 'Invalid email'}
                  </p>
                )}
              </div>

              {/* Password Field */}
              <div>
                <label className="block text-sm font-medium text-white mb-2">
                  Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-va-neutral-400 h-5 w-5" />
                  <input
                    type={showPassword ? "text" : "password"}
                    autoComplete="new-password"
                    {...register('password', { 
                      required: 'Password is required',
                      minLength: {
                        value: 6,
                        message: 'Password must be at least 6 characters'
                      }
                    })}
                    className="w-full pl-10 pr-12 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-va-neutral-300 focus:outline-none focus:ring-2 focus:ring-brand-blue focus:border-transparent"
                    placeholder="Enter your password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-va-neutral-400 hover:text-white transition-colors"
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5" />
                    ) : (
                      <Eye className="h-5 w-5" />
                    )}
                  </button>
                </div>
                {errors.password && (
                  <p className="mt-1 text-sm text-red-300">
                    {errors.password.message}
                  </p>
                )}
              </div>
            </div>

            {/* Forgot Password Link */}
            <div className="text-right">
              <button
                type="button"
                onClick={() => handleForgotPassword()}
                className="text-sm text-white hover:text-va-primary-100 underline transition-colors"
              >
                Forgot Password?
              </button>
            </div>


            <button
              type="submit"
              disabled={loading || !email}
              className="w-full bg-white text-brand-blue py-3 px-4 rounded-lg font-semibold hover:bg-va-primary-50 focus:outline-none focus:ring-2 focus:ring-brand-blue focus:ring-offset-2 focus:ring-offset-transparent transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {loading ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-brand-blue"></div>
              ) : (
                <>
                  Sign In
                  <span className="ml-2">→</span>
                </>
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Login;